import CodeBrowserIcon from "@redotech/redo-icons/code-browser.svg";
import {
  CustomerActivityType,
  CustomerDataSource,
  SegmentConditionCategory,
} from "@redotech/redo-model/marketing/segments/segment-types";
import { filterTruthy } from "@redotech/util/array";
import {
  segmentCategoryToFriendly,
  segmentCategoryToIcon,
} from "../segment-category";
import { Category, Item } from "../trifold-selector";

const customerActivityToActivity: Record<CustomerActivityType, Activity> = {
  [CustomerActivityType.RECEIVED_EMAIL]: {
    name: "Received email",
    description:
      "An email was sent to this person and their inbox received it.",
    source: CustomerDataSource.REDO,
    type: CustomerActivityType.RECEIVED_EMAIL,
  },
  [CustomerActivityType.OPENED_EMAIL]: {
    name: "Opened email",
    description: "An email was sent to this person and they opened it.",
    source: CustomerDataSource.REDO,
    type: CustomerActivityType.OPENED_EMAIL,
  },
  [CustomerActivityType.CLICKED_EMAIL]: {
    name: "Clicked email",
    description: "Person clicked a link in the email they received.",
    source: CustomerDataSource.REDO,
    type: CustomerActivityType.CLICKED_EMAIL,
  },
  [CustomerActivityType.RECEIVED_TEXT]: {
    name: "Received text",
    description: "A text was sent to this person and their device received it.",
    source: CustomerDataSource.REDO,
    type: CustomerActivityType.RECEIVED_TEXT,
  },
  [CustomerActivityType.CLICKED_TEXT]: {
    name: "Clicked text",
    description: "Person clicked a link in the text they received.",
    source: CustomerDataSource.REDO,
    type: CustomerActivityType.CLICKED_TEXT,
  },
  [CustomerActivityType.VIEWED_PRODUCT_SHOPIFY]: {
    name: "Viewed product (Shopify)",
    description: "Person saw a product on your Shopify store.",
    source: CustomerDataSource.SHOPIFY,
    type: CustomerActivityType.VIEWED_PRODUCT_SHOPIFY,
  },
  [CustomerActivityType.VIEWED_PRODUCT_COMMENTSOLD]: {
    name: "Viewed product (CommentSold)",
    description: "Person saw a product on your CommentSold store.",
    source: CustomerDataSource.COMMENTSOLD,
    type: CustomerActivityType.VIEWED_PRODUCT_COMMENTSOLD,
  },
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_SHOPIFY]: {
    name: "Added product to cart (Shopify)",
    description: "Person added a product to their cart on Shopify.",
    source: CustomerDataSource.SHOPIFY,
    type: CustomerActivityType.ADDED_PRODUCT_TO_CART_SHOPIFY,
  },
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_COMMENTSOLD]: {
    name: "Added product to cart (CommentSold)",
    description: "Person added a product to their cart on CommentSold.",
    source: CustomerDataSource.COMMENTSOLD,
    type: CustomerActivityType.ADDED_PRODUCT_TO_CART_COMMENTSOLD,
  },
  [CustomerActivityType.ORDER_PLACED_SHOPIFY]: {
    name: "Placed order (Shopify)",
    description: "Person placed an order on your Shopify store.",
    source: CustomerDataSource.SHOPIFY,
    type: CustomerActivityType.ORDER_PLACED_SHOPIFY,
  },
  [CustomerActivityType.ORDER_PLACED_COMMENTSOLD]: {
    name: "Placed order (CommentSold)",
    description: "Person placed an order on your CommentSold store.",
    source: CustomerDataSource.COMMENTSOLD,
    type: CustomerActivityType.ORDER_PLACED_COMMENTSOLD,
  },
  [CustomerActivityType.CHECKOUT_STARTED_SHOPIFY]: {
    name: "Started checkout (Shopify)",
    description: "Person made it to the checkout screen on Shopify.",
    source: CustomerDataSource.SHOPIFY,
    type: CustomerActivityType.CHECKOUT_STARTED_SHOPIFY,
  },
  [CustomerActivityType.CHECKOUT_STARTED_COMMENTSOLD]: {
    name: "Started checkout (CommentSold)",
    description: "Person made it to the checkout screen on CommentSold.",
    source: CustomerDataSource.COMMENTSOLD,
    type: CustomerActivityType.CHECKOUT_STARTED_COMMENTSOLD,
  },
  [CustomerActivityType.ACTIVE_ON_SITE_SHOPIFY]: {
    name: "Been active on site (Shopify)",
    description: "Person visited your Shopify site.",
    source: CustomerDataSource.SHOPIFY,
    type: CustomerActivityType.ACTIVE_ON_SITE_SHOPIFY,
  },
  [CustomerActivityType.ACTIVE_ON_SITE_COMMENTSOLD]: {
    name: "Been active on site (CommentSold)",
    description: "Person visited your CommentSold site.",
    source: CustomerDataSource.COMMENTSOLD,
    type: CustomerActivityType.ACTIVE_ON_SITE_COMMENTSOLD,
  },
  [CustomerActivityType.COLLECTION_VIEWED_SHOPIFY]: {
    name: "Viewed collection (Shopify)",
    description: "Person viewed a collection on your Shopify store.",
    source: CustomerDataSource.SHOPIFY,
    type: CustomerActivityType.COLLECTION_VIEWED_SHOPIFY,
  },
  [CustomerActivityType.COLLECTION_VIEWED_COMMENTSOLD]: {
    name: "Viewed collection (CommentSold)",
    description: "Person viewed a collection on your CommentSold store.",
    source: CustomerDataSource.COMMENTSOLD,
    type: CustomerActivityType.COLLECTION_VIEWED_COMMENTSOLD,
  },
  [CustomerActivityType.RETURN_PROCESSED]: {
    name: "Processed return",
    description: "A return for this person was processed.",
    source: CustomerDataSource.REDO,
    type: CustomerActivityType.RETURN_PROCESSED,
  },
  [CustomerActivityType.SUBSCRIBED_TO_BACK_IN_STOCK]: {
    name: "Subscribed to back-in-stock notifications",
    description:
      "Person subscribed to be notified when a product is back in stock.",
    source: CustomerDataSource.REDO,
    type: CustomerActivityType.SUBSCRIBED_TO_BACK_IN_STOCK,
  },
  [CustomerActivityType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION]: {
    name: "Triggered back-in-stock notification",
    description:
      "Person received a notification that a product is back in stock.",
    source: CustomerDataSource.REDO,
    type: CustomerActivityType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION,
  },
};

export interface Activity extends Item {
  name: string;
  description: string;
  source: CustomerDataSource;
  type: CustomerActivityType;
}

export const CUSTOM_EVENT_ACTIVITY_TYPE = "custom_event" as const;

export interface CustomActivity extends Item {
  name: string;
  description: string;
  source: CustomerDataSource;
  type: typeof CUSTOM_EVENT_ACTIVITY_TYPE;
}

export type AnyActivity = Activity | CustomActivity;

export function isCustomActivity(
  activity: AnyActivity,
): activity is CustomActivity {
  return activity.type === CUSTOM_EVENT_ACTIVITY_TYPE;
}

export type ActivityCategory = Category<AnyActivity>;

const activityToCategory: Record<
  CustomerActivityType,
  SegmentConditionCategory
> = {
  [CustomerActivityType.RECEIVED_EMAIL]: SegmentConditionCategory.MARKETING,
  [CustomerActivityType.OPENED_EMAIL]: SegmentConditionCategory.MARKETING,
  [CustomerActivityType.CLICKED_EMAIL]: SegmentConditionCategory.MARKETING,
  [CustomerActivityType.RECEIVED_TEXT]: SegmentConditionCategory.MARKETING,
  [CustomerActivityType.CLICKED_TEXT]: SegmentConditionCategory.MARKETING,
  [CustomerActivityType.VIEWED_PRODUCT_SHOPIFY]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.VIEWED_PRODUCT_COMMENTSOLD]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_SHOPIFY]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_COMMENTSOLD]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.ORDER_PLACED_SHOPIFY]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.ORDER_PLACED_COMMENTSOLD]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.CHECKOUT_STARTED_SHOPIFY]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.CHECKOUT_STARTED_COMMENTSOLD]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.ACTIVE_ON_SITE_SHOPIFY]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.ACTIVE_ON_SITE_COMMENTSOLD]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.COLLECTION_VIEWED_SHOPIFY]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.COLLECTION_VIEWED_COMMENTSOLD]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.RETURN_PROCESSED]: SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.SUBSCRIBED_TO_BACK_IN_STOCK]:
    SegmentConditionCategory.SHOPPING,
  [CustomerActivityType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION]:
    SegmentConditionCategory.SHOPPING,
};

function getItemsByCategory(
  category: SegmentConditionCategory,
  availableDataSourcesForOrders: CustomerDataSource[] = [
    CustomerDataSource.REDO,
    CustomerDataSource.SHOPIFY,
    CustomerDataSource.COMMENTSOLD,
  ],
  availableDataSourcesForMarketing: CustomerDataSource[] = [
    CustomerDataSource.REDO,
    CustomerDataSource.SHOPIFY,
    CustomerDataSource.COMMENTSOLD,
  ],
): Activity[] {
  const items = filterTruthy(
    Object.values(CustomerActivityType).map((type) => {
      if (activityToCategory[type] !== category) {
        return null;
      }
      const activity = customerActivityToActivity[type];

      // Order-placed activities use enabled integrations (regardless of marketing settings)
      if (
        type === CustomerActivityType.ORDER_PLACED_SHOPIFY ||
        type === CustomerActivityType.ORDER_PLACED_COMMENTSOLD
      ) {
        return availableDataSourcesForOrders.includes(activity.source)
          ? activity
          : null;
      }

      // Other CommentSold activities require marketing settings enabled
      if (activity.source === CustomerDataSource.COMMENTSOLD) {
        return availableDataSourcesForMarketing.includes(activity.source)
          ? activity
          : null;
      }

      // All other activities (Shopify non-order, REDO) use enabled integrations
      return availableDataSourcesForOrders.includes(activity.source)
        ? activity
        : null;
    }),
  );
  return items.sort((a, b) => a.name.localeCompare(b.name));
}

const customEventActivity: CustomActivity = {
  name: "Custom event",
  description:
    "Filter by a custom event you've sent via the Redo Custom Events API.",
  source: CustomerDataSource.REDO,
  type: CUSTOM_EVENT_ACTIVITY_TYPE,
};

export function getCategories(
  availableDataSourcesForOrders: CustomerDataSource[] = [
    CustomerDataSource.REDO,
    CustomerDataSource.SHOPIFY,
    CustomerDataSource.COMMENTSOLD,
  ],
  availableDataSourcesForMarketing: CustomerDataSource[] = [
    CustomerDataSource.REDO,
    CustomerDataSource.SHOPIFY,
    CustomerDataSource.COMMENTSOLD,
  ],
): ActivityCategory[] {
  return [
    {
      name: segmentCategoryToFriendly[SegmentConditionCategory.MARKETING],
      Icon: segmentCategoryToIcon[SegmentConditionCategory.MARKETING],
      items: getItemsByCategory(
        SegmentConditionCategory.MARKETING,
        availableDataSourcesForOrders,
        availableDataSourcesForMarketing,
      ),
    },
    {
      name: segmentCategoryToFriendly[SegmentConditionCategory.SHOPPING],
      Icon: segmentCategoryToIcon[SegmentConditionCategory.SHOPPING],
      items: getItemsByCategory(
        SegmentConditionCategory.SHOPPING,
        availableDataSourcesForOrders,
        availableDataSourcesForMarketing,
      ),
    },
    { name: "Custom", Icon: CodeBrowserIcon, items: [customEventActivity] },
  ];
}

export const categories = getCategories();

export function getActivity(name: CustomerActivityType): Activity {
  return customerActivityToActivity[name];
}
